import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Group } from "@mantine/core";

/**
 * Filters are edited as a draft and only take effect on "Применить" (or Enter):
 * the list is re-queried from the server with the applied values, never
 * filtered client-side. `applied` is the source of truth; the draft resets to
 * it whenever it changes from outside (URL, reset, another tab of the page).
 */
export function useFilterDraft<T extends object>(applied: T) {
  const key = JSON.stringify(applied);
  const [draft, setDraft] = useState<T>(applied);
  // Reset on a change of value, not of identity: callers rebuild `applied` every render.
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  useEffect(() => setDraft(appliedRef.current), [key]);
  return {
    draft,
    patch: (p: Partial<T>) => setDraft(d => ({ ...d, ...p })),
    dirty: JSON.stringify(draft) !== key,
  };
}

export function FilterBar({ onApply, onReset, dirty, children }: {
  onApply: () => void;
  onReset?: () => void;
  /** The draft differs from what is applied: highlights "Применить". */
  dirty: boolean;
  children: ReactNode;
}) {
  return (
    <form onSubmit={e => { e.preventDefault(); onApply(); }}>
      <Group gap="md" align="flex-end" wrap="wrap">
        {children}
        <Group gap="xs" wrap="nowrap">
          <Button type="submit" variant={dirty ? "filled" : "light"}>Применить</Button>
          {onReset && <Button type="button" variant="subtle" color="gray" onClick={onReset}>Сбросить</Button>}
        </Group>
      </Group>
    </form>
  );
}
